import React, { useState } from 'react';
import { 
    Box, Typography, Card, CardContent, Button, TextField, 
    Alert, Link, Paper 
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import API_BASE_URL from '../config/api';

export default function TenantLoginView({ subdomain, onLoginSuccess, onBackToLanding }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const activeSubdomain = subdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || 'demo';
            localStorage.setItem('dinexa_active_tenant_subdomain', activeSubdomain);

            const response = await fetch(`${API_BASE_URL}/tenant/login?tenant=${encodeURIComponent(activeSubdomain)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSubdomain
                },
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                const finalSubdomain = data.user?.subdomain || activeSubdomain;
                const branchId = data.user?.branch_id || 1;
                localStorage.setItem('dinexa_active_tenant_subdomain', finalSubdomain);
                localStorage.setItem('dinexa_active_branch_id', String(branchId));

                onLoginSuccess({
                    id: data.user.id,
                    name: data.user.name,
                    email: data.user.email,
                    role: data.user.role,
                    branch_id: branchId,
                    branch_name: data.user.branch_name,
                    branch_address: data.user.branch_address,
                    branch_phone: data.user.branch_phone,
                    upi_id: data.user.upi_id,
                    upi_merchant_name: data.user.upi_merchant_name,
                    tenant_name: data.user.tenant_name,
                    receipt_template: data.user.receipt_template,
                    allowed_menus: data.user.allowed_menus,
                    is_multi_branch: data.user.is_multi_branch,
                    max_tables: data.user.max_tables,
                    max_sections: data.user.max_sections,
                    subscribed_modules: data.user?.subscribed_modules || data.subscribed_modules,
                    subdomain: finalSubdomain,
                    gst_enabled: data.user?.gst_enabled,
                    branches: data.user?.branches || []
                });
            } else {
                setError(data.error || 'अवैध ईमेल किंवा पासवर्ड. कृपया पुन्हा प्रयत्न करा.');
            }
        } catch (err) {
            console.error(err);
            setError(`सर्व्हरशी संपर्क साधण्यात अडचण आली: ${err.message || 'कृपया बॅकएंड (Laravel) चालू असल्याची खात्री करा.'}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box 
            sx={{ 
                minHeight: '100vh', 
                bgcolor: '#f8fafc', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center',
                p: 2 
            }}
        >
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <img 
                    src="/logo192.png" 
                    alt="Dinexa Logo" 
                    style={{ width: 64, height: 64, borderRadius: 12, marginBottom: 16 }} 
                    onError={(e) => { e.target.style.display = 'none'; }} 
                />
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#008751', mb: 0.5 }}>
                    DinexaPOS
                </Typography>
                <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', tracking: '0.1em' }}>
                    {subdomain} Terminal
                </Typography>
            </Box>

            <Card sx={{ width: '100%', maxWidth: 420, borderRadius: 4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockOpenIcon color="primary" /> Store Sign In
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleLoginSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                required
                                fullWidth
                                label="User Email Address"
                                type="email"
                                size="small"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                inputProps={{
                                    autoComplete: 'off',
                                    autoCorrect: 'off',
                                    autoCapitalize: 'none',
                                    spellCheck: 'false',
                                    style: { touchAction: 'manipulation' }
                                }}
                            />
                            <TextField
                                required
                                fullWidth
                                label="Security Password"
                                type="password"
                                size="small"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                inputProps={{
                                    autoComplete: 'off',
                                    autoCorrect: 'off',
                                    style: { touchAction: 'manipulation' }
                                }}
                            />

                            <Button 
                                type="submit" 
                                variant="contained" 
                                color="primary"
                                fullWidth
                                disabled={loading}
                                sx={{ fontWeight: 800, height: 40 }}
                            >
                                {loading ? 'Authenticating...' : 'Secure Login'}
                            </Button>
                        </Box>
                    </form>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Link 
                            component="button"
                            variant="caption" 
                            onClick={onBackToLanding}
                            sx={{ color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700, textDecoration: 'none', '&:hover': { color: '#008751' } }}
                        >
                            <ArrowBackIcon sx={{ fontSize: 12 }} /> Back to Dinexa SaaS Portal
                        </Link>
                    </Box>
                </CardContent>
            </Card>

            <Typography variant="caption" sx={{ mt: 4, color: '#94a3b8', fontWeight: 600 }}>
                Securely connected to: {subdomain}.dinexa.in
            </Typography>
        </Box>
    );
}
